import React from "react";
import SimpleStickySwap from "../components/SimpleStickySwap";
import DashboardShowcaseSection from "../components/DashboardShowcaseSection"
import FAQSection from "../components/FAQSection";
import SteffiLanding from "../components/SteffiLanding";
import HeaderTransparent from "../components/HeaderTransparent";
import { useEffect, useState, useRef } from "react";
import FeaturesSixGrid from "../components/FeaturesSixGrid";
import SpeechCardsWithDevice from "../components/SpeechCardsWithDevice";
import ContactFooterSection from "../components/contactFooter/ContactFooterSection";
import Lenis from "@studio-freight/lenis";
import Nav from "../../Steffi/sections/Nav";
import "@fontsource-variable/manrope";
import "@fontsource-variable/inter";
import "../website.css";


/** 'up' | 'down' with a small threshold to avoid jitter. */
export function useScrollDirection(threshold = 6) {
  const [dir, setDir] = useState("down");
  const lastY = useRef(typeof window !== "undefined" ? window.scrollY : 0);

  useEffect(() => {
    function onScroll() {
      const y = window.scrollY || 0;
      const diff = y - lastY.current;
      if (Math.abs(diff) > threshold) {
        setDir(diff > 0 ? "down" : "up");
        lastY.current = y;
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return dir;
}


function SmartHeader({ show, children }) {
  return (
    <div
      className={[
        "fixed top-0 left-0 right-0 z-50",
        "transition-all duration-500 ease-out",
        "will-change-transform", // smoother
        show ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-full pointer-events-none",
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function useScrolledPast(ref, offset = 0) {
  const [isPast, setIsPast] = useState(false);

  useEffect(() => {
    function handleScroll() {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      // when bottom of landing is above viewport top + offset
      setIsPast(rect.bottom <= offset);
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [ref, offset]);

  return isPast;
}

export default function SteffiLanding2() {

    const scrollRef = useRef(null);

    const handleMehrErfahren = () => {
      scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    useEffect(() => {
    const lenis = new Lenis({
      duration: 0.5, // 👈 increase this for slower scroll (default ~1)
      smooth: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);
  }, []);

  const landingRef = useRef(null);

  // Only allow header after the landing hero is scrolled past
  const pastLanding = useScrolledPast(landingRef, 0);

  // Show header only while scrolling UP (and not right at very top)
  const dir = useScrollDirection(8);
  const showHeader = pastLanding && dir === "up" && (typeof window !== "undefined" ? window.scrollY > 80 : false);

  return (
    <div className="care-site w-full text-white">
      <div className="steffi sm:hidden">
        <Nav variant="care" />
      </div>

      {/* Landing hero (no header here) */}
      <div ref={landingRef}>
      <SteffiLanding onMehrErfahren={handleMehrErfahren} />
      </div>

      {/* Smart header – appears only when scrolling UP after the hero */}
      <SmartHeader show={showHeader}>
        <HeaderTransparent />
      </SmartHeader>

      {/* Content */}
      <FeaturesSixGrid/>
      <SimpleStickySwap />
      <section className="relative overflow-hidden bg-[#eef3fb]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.95),_rgba(238,243,251,0.65)_42%,_rgba(227,236,249,0.9)_100%)]" />
        <div className="pointer-events-none absolute left-[-8%] top-20 h-56 w-56 rounded-full bg-white/60 blur-3xl" />
        <div className="pointer-events-none absolute right-[-6%] top-[28rem] h-72 w-72 rounded-full bg-[#d9e7ff]/70 blur-3xl" />
        <div className="relative">
          <DashboardShowcaseSection />
          <SpeechCardsWithDevice />
        </div>
      </section>
      <div className="w-full h-20 bg-[#ffffff]" />
      <FAQSection />
      <section id="steffi-scroll" ref={scrollRef}>
        <ContactFooterSection />
      </section>
    </div>
  );
}
